import React, { useRef, useEffect, useState } from 'react';
import { LayoutStyle, LayoutType, Promotion, TextStyle, PanfletoElementPlacements } from '../../../shared/types';
import { Maximize2, X, Loader } from 'lucide-react';
import {
  getPanfletoBackgroundCssColor,
  mergePanfletoContent,
  panfletoHasExplicitBackgroundColor,
  panfletoHasFreeformPlacements,
} from '../../banners/utils/panfletoContentUtils';
import { PanfletoFlyerContent } from '../../promotions/components/PanfletoFlyerContent';

interface StylePreviewProps {
  promotion: Promotion;
  layoutType: LayoutType;
  layoutStyle: LayoutStyle;
  /** Panfleto: alinhar vitrine/borda compartilhada à tela ter produtos (preview não usa promo fictício como “real”). */
  panfletoHasProductsOnScreen?: boolean;
  /** Panfleto: foto de fundo remota ainda sendo buscada na API. */
  panfletoFotoLoading?: boolean;
}

const StylePreview: React.FC<StylePreviewProps> = ({
  promotion,
  layoutType,
  layoutStyle,
  panfletoHasProductsOnScreen,
  panfletoFotoLoading = false,
}) => {
  const PANFLETO_VIRTUAL_WIDTH = 1920;
  const PANFLETO_VIRTUAL_HEIGHT = 1080;
  const panfletoViewportRef = useRef<HTMLDivElement>(null);
  const panfletoOverlayViewportRef = useRef<HTMLDivElement>(null);
  const [panfletoScale, setPanfletoScale] = useState(1);
  const [panfletoOverlayScale, setPanfletoOverlayScale] = useState(1);
  const [isPanfletoPreviewExpanded, setIsPanfletoPreviewExpanded] = useState(false);
  const [panfletoBgImageReady, setPanfletoBgImageReady] = useState(false);

  const panfletoBgImageUrl =
    layoutType === 'panfleto' && layoutStyle.productContainer?.backgroundImageVisible !== false
      ? layoutStyle.productContainer?.backgroundImage
      : undefined;

  useEffect(() => {
    if (!panfletoBgImageUrl) {
      setPanfletoBgImageReady(false);
      return;
    }
    setPanfletoBgImageReady(false);
    let cancelled = false;
    const img = new Image();
    const markReady = () => {
      if (!cancelled) setPanfletoBgImageReady(true);
    };
    img.onload = markReady;
    img.onerror = markReady;
    img.src = panfletoBgImageUrl;
    if (img.complete) {
      markReady();
    }
    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [panfletoBgImageUrl]);

  const calculateDiscount = (original: number, current: number) => {
    if (!original || !current) return 0;
    return Math.round(((original - current) / original) * 100);
  };

  const discount = promotion.originalPrice
    ? calculateDiscount(promotion.originalPrice, promotion.price)
    : 0;

  // Função auxiliar para converter hex para rgba - EXATAMENTE como no PromoCard
  const hexToRgba = (hex: string, alpha: number): string => {
    let cleanHex = hex.replace('#', '');
    // Expandir hex de 3 para 6 caracteres se necessário
    if (cleanHex.length === 3) {
      cleanHex = cleanHex.split('').map(char => char + char).join('');
    }
    if (cleanHex.length === 6) {
      const r = parseInt(cleanHex.substr(0, 2), 16);
      const g = parseInt(cleanHex.substr(2, 2), 16);
      const b = parseInt(cleanHex.substr(4, 2), 16);
      if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      }
    }
    return hex; // Retornar original se não conseguir converter
  };

  const getContainerStyle = () => {
    // Garantir que sempre temos um productContainer válido
    if (!layoutStyle || !layoutStyle.productContainer) {
      return {
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        padding: '16px',
        margin: '0px',
      };
    }

    const containerStyle = layoutStyle.productContainer;

    // Aplicar opacidade e blur no background do card (não na imagem de fundo)
    const opacity = containerStyle.backgroundImageOpacity !== undefined
      ? containerStyle.backgroundImageOpacity / 100
      : 1;
    const blur = containerStyle.backgroundImageBlur ?? 0;

    let backgroundStyle: React.CSSProperties = {};

    // Aplicar opacidade diretamente nas cores do gradiente ou background - EXATAMENTE como no PromoCard
    if (containerStyle.useGradient && containerStyle.gradientStart && containerStyle.gradientEnd) {
      // Para gradiente, aplicar opacidade nas cores do gradiente
      const startColor = opacity < 1
        ? hexToRgba(containerStyle.gradientStart, opacity)
        : containerStyle.gradientStart;
      const endColor = opacity < 1
        ? hexToRgba(containerStyle.gradientEnd, opacity)
        : containerStyle.gradientEnd;
      backgroundStyle.background = `linear-gradient(to right, ${startColor}, ${endColor})`;
    } else if (containerStyle.backgroundColor) {
      // Para background color sólido, aplicar opacidade via rgba
      if (opacity < 1) {
        backgroundStyle.backgroundColor = hexToRgba(containerStyle.backgroundColor, opacity);
      } else {
        backgroundStyle.backgroundColor = containerStyle.backgroundColor;
      }
    } else {
      // Fallback: usar cor branca se não houver backgroundColor definido
      backgroundStyle.backgroundColor = '#ffffff';
    }

    return {
      ...backgroundStyle,
      borderRadius: containerStyle.borderRadius ? `${containerStyle.borderRadius}px` : undefined,
      padding: `${containerStyle.paddingTop || 0}px ${containerStyle.paddingRight || 0}px ${containerStyle.paddingBottom || 0}px ${containerStyle.paddingLeft || 0}px`,
      margin: `${containerStyle.marginTop || 0}px ${containerStyle.marginRight || 0}px ${containerStyle.marginBottom || 0}px ${containerStyle.marginLeft || 0}px`,
      // Aplicar blur apenas se > 0 (blur no background do card)
      backdropFilter: blur > 0 ? `blur(${blur}px)` : undefined,
      // Garantir que o background cubra completamente as bordas arredondadas
      backgroundClip: 'border-box' as const,
      overflow: 'hidden' as const,
      // Garantir que não haja borda visível que mostre a forma retangular
      border: 'none',
      outline: 'none',
    };
  };


  // Função para formatar números no padrão brasileiro (igual ao PromoCard)
  const formatBrazilianNumber = (value: number | undefined, showCurrency: boolean = true): string => {
    const numValue = value ?? 0;
    const formattedNumber = numValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return showCurrency ? `R$ ${formattedNumber}` : formattedNumber;
  };

  // Get text style with defaults - EXATAMENTE como no PromoCard
  // IMPORTANTE: Quando fontSize é 0 (campo vazio no editor), usar 1px como fallback mínimo
  const getTextStyle = (styleKey: keyof Omit<LayoutStyle, 'productContainer' | 'priceBox'>) => {
    if (!layoutStyle || !layoutStyle[styleKey]) {
      // Retornar estilo padrão básico
      return {
        color: '#000000',
        fontSize: '16px',
        fontFamily: 'Arial, sans-serif',
      };
    }

    const style = layoutStyle[styleKey] as TextStyle;
    const verticalStretchValue = style.verticalStretch || 100;
    const hasVerticalStretch = verticalStretchValue > 100;

    // Para unidade, não aplicar transform aqui se houver verticalOffset (será aplicado separadamente)
    const isUnit = styleKey === 'unit';
    const hasVerticalOffset = isUnit && (style.verticalOffset !== undefined && style.verticalOffset !== 0);

    // IMPORTANTE: Quando fontSize é 0 ou undefined, usar 1 como fallback mínimo para visualização
    // Isso permite que o usuário apague o campo e digite um novo valor
    const fontSize = style.fontSize || 1;

    return {
      color: style.color || '#000000',
      fontSize: `${fontSize}px`,
      fontFamily: style.fontFamily || 'Arial, sans-serif',
      lineHeight: style.lineHeight || 1.2,
      letterSpacing: style.letterSpacing ? `${style.letterSpacing}px` : 'normal',
      fontWeight: style.fontWeight || 400,
      // Transform no texto do preço precisa de inline-block para renderizar corretamente
      display: (styleKey === 'price' || hasVerticalStretch) ? 'inline-block' : undefined,
      // Não aplicar transform se for unidade com verticalOffset (será aplicado separadamente)
      transform: (hasVerticalOffset ? undefined : (hasVerticalStretch
        ? `scaleY(${verticalStretchValue / 100})`
        : undefined)),
      transformOrigin: (hasVerticalStretch || hasVerticalOffset) ? 'center' : undefined,
    };
  };

  // Hooks para truncamento do nome (devem estar no topo, antes de qualquer retorno condicional)
  const containerRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLHeadingElement>(null);
  const unitRef = useRef<HTMLSpanElement>(null);
  const [truncatedName, setTruncatedName] = useState(promotion.name);

  // Effect para truncar texto com apenas um ponto (apenas para layout simple2col)
  useEffect(() => {
    // Só executar para layout simple2col
    if (layoutType !== 'simple2col') {
      setTruncatedName(promotion.name);
      return;
    }

    const containerElement = containerRef.current;
    const nameElement = nameRef.current;
    const unitElement = unitRef.current;
    
    if (!containerElement || !nameElement || !unitElement) {
      // Se os elementos ainda não estão disponíveis, manter o nome original
      setTruncatedName(promotion.name);
      return;
    }

    const checkTruncation = () => {
      // Criar um elemento temporário invisível para medir sem afetar o DOM visível
      const tempElement = document.createElement('span');
      tempElement.style.visibility = 'hidden';
      tempElement.style.position = 'absolute';
      tempElement.style.whiteSpace = 'nowrap';
      tempElement.style.fontSize = window.getComputedStyle(nameElement).fontSize;
      tempElement.style.fontFamily = window.getComputedStyle(nameElement).fontFamily;
      tempElement.style.fontWeight = window.getComputedStyle(nameElement).fontWeight;
      tempElement.style.letterSpacing = window.getComputedStyle(nameElement).letterSpacing;
      document.body.appendChild(tempElement);
      
      // Obter a largura disponível do container pai
      const containerWidth = containerElement.clientWidth;
      
      // Obter a largura ocupada pela unidade (incluindo margin)
      const unitWidth = unitElement.offsetWidth;
      const unitMargin = parseInt(window.getComputedStyle(unitElement).marginLeft) || 0;
      const totalUnitWidth = unitWidth + unitMargin;
      
      // Calcular o espaço disponível para o nome (container - unidade)
      const availableWidth = containerWidth - totalUnitWidth;
      
      // Testar se o texto original cabe
      tempElement.textContent = promotion.name;
      const fullTextWidth = tempElement.offsetWidth;
      
      if (fullTextWidth > availableWidth) {
        // Precisa truncar - encontrar o tamanho correto
        let text = promotion.name;
        while (text.length > 0) {
          const testText = text + '.';
          tempElement.textContent = testText;
          if (tempElement.offsetWidth <= availableWidth) {
            setTruncatedName(testText);
            document.body.removeChild(tempElement);
            return;
          }
          text = text.slice(0, -1);
        }
        setTruncatedName('.');
      } else {
        setTruncatedName(promotion.name);
      }
      
      document.body.removeChild(tempElement);
    };

    // Usar requestAnimationFrame para garantir que o DOM esteja renderizado, mas de forma mais rápida
    const rafId = requestAnimationFrame(() => {
      checkTruncation();
    });
    
    // Verificar quando a janela redimensionar
    window.addEventListener('resize', checkTruncation);
    
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [layoutType, promotion.name, promotion.unit]);

  useEffect(() => {
    if (layoutType !== 'panfleto') return;
    const el = panfletoViewportRef.current;
    if (!el) return;
    const updateScale = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (!w || !h) return;
      const next = Math.min(w / PANFLETO_VIRTUAL_WIDTH, h / PANFLETO_VIRTUAL_HEIGHT);
      setPanfletoScale(next > 0 ? next : 1);
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(el);
    return () => observer.disconnect();
  }, [layoutType]);

  useEffect(() => {
    if (!isPanfletoPreviewExpanded) return;
    const el = panfletoOverlayViewportRef.current;
    if (!el) return;
    const updateScale = () => {
      const w = el.clientWidth;
      if (!w) return;
      // No modo ampliado, preencher pela largura para eliminar barras laterais.
      const next = w / PANFLETO_VIRTUAL_WIDTH;
      setPanfletoOverlayScale(next > 0 ? next : 1);
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isPanfletoPreviewExpanded]);

  // Simple 2 column layout preview
  if (layoutType === 'simple2col') {
    // Determinar se deve usar o borderRadius da classe ou do estilo - EXATAMENTE como no PromoCard
    const useDefaultBorderRadius = !layoutStyle || !layoutStyle.productContainer || layoutStyle.productContainer.borderRadius === undefined;
    const hasNameVerticalStretch = layoutStyle.productName?.verticalStretch && layoutStyle.productName.verticalStretch > 100;
    const containerStyle = layoutStyle.productContainer || {};
    const bgImage = containerStyle.backgroundImage;
    const bgVisible = containerStyle.backgroundImageVisible !== false;

    return (
      <div className="w-full border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="bg-gray-50 p-3 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-700">Pré-visualização - Layout: Lista com colunas customizáveis</h3>
        </div>
        {/* Container com imagem de fundo (se houver) */}
        <div
          className="p-4 relative"
          style={{
            backgroundColor: bgImage && bgVisible ? '#000000' : '#ffffff',
            backgroundImage: bgImage && bgVisible ? `url(${bgImage})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            minHeight: '200px',
          }}
        >
          {/* Container do produto com opacidade/blur - EXATAMENTE como no PromoCard */}
          {/* Verificar se a opacidade da imagem de fundo é 0% para remover a sombra/borda */}
          {(() => {
            const bgImageOpacity = containerStyle.backgroundImageOpacity ?? 100;
            const shouldHideShadow = bgImage && bgVisible && bgImageOpacity === 0;
            
            return (
              <div
                style={{
                  ...getContainerStyle(),
                  // Remover boxShadow quando a opacidade for 0% para que o texto fique integrado na imagem
                  boxShadow: shouldHideShadow ? 'none' : '0 4px 6px rgba(0, 0, 0, 0.1)',
                }}
                className={`${shouldHideShadow ? '' : 'shadow-sm'} px-3 py-4 ${shouldHideShadow ? '' : 'hover:shadow-md'} transition-all h-full w-full ${useDefaultBorderRadius ? 'rounded-lg' : ''}`}
              >
            {/* Content Layer - EXATAMENTE como no PromoCard */}
            <div className="flex items-center justify-between w-full h-full">
              <div
                ref={containerRef}
                className={`flex ${layoutStyle?.unit?.verticalAlignment === 'bottom' ? 'items-baseline' : 'items-center'} ${hasNameVerticalStretch || (layoutStyle?.unit?.verticalAlignment && layoutStyle?.unit?.verticalAlignment !== 'bottom') ? 'h-full' : ''} mr-4 overflow-hidden flex-1`}
              >
                <h3
                  ref={nameRef}
                  className="font-bold text-3xl text-gray-800 leading-none"
                  style={{
                    ...getTextStyle('productName'),
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    display: hasNameVerticalStretch ? 'flex' : 'block',
                    alignItems: hasNameVerticalStretch ? 'center' : undefined,
                  }}
                >
                  {truncatedName}
                </h3>
                {/* Unidade ao lado direito do nome, bem próxima e alinhada verticalmente - EXATAMENTE como no PromoCard */}
                <span
                  ref={unitRef}
                  className="text-gray-500 ml-1"
                  style={{
                    ...getTextStyle('unit'),
                    // Aplicar alinhamento vertical ou offset se configurado - EXATAMENTE como no PromoCard
                    alignSelf: (() => {
                      const unitStyle = layoutStyle?.unit;
                      const verticalAlignment = unitStyle?.verticalAlignment;

                      // Quando 'bottom', alinhar na linha de base inferior do texto (baseline)
                      // Isso faz o "UN" ficar alinhado na parte inferior do texto do produto
                      if (verticalAlignment === 'bottom') return 'baseline';
                      // 'center' ou undefined = centralizado (padrão)
                      return 'center';
                    })(),
                    transform: (() => {
                      const unitStyle = layoutStyle?.unit;
                      const verticalOffset = unitStyle?.verticalOffset; // Para compatibilidade com versões antigas
                      const verticalStretch = unitStyle?.verticalStretch || 100;
                      const hasVerticalStretch = verticalStretch > 100;

                      // Se houver verticalAlignment, não usar offset em pixels
                      if (unitStyle?.verticalAlignment) {
                        // Apenas aplicar stretch se necessário
                        return hasVerticalStretch ? `scaleY(${verticalStretch / 100})` : undefined;
                      }

                      // Compatibilidade: usar offset em pixels se não houver alignment
                      if (verticalOffset !== undefined && verticalOffset !== 0) {
                        if (hasVerticalStretch) {
                          return `translateY(${verticalOffset}px) scaleY(${verticalStretch / 100})`;
                        }
                        return `translateY(${verticalOffset}px)`;
                      }

                      return hasVerticalStretch ? `scaleY(${verticalStretch / 100})` : undefined;
                    })(),
                    transformOrigin: 'center',
                  }}
                >
                  {promotion.unit}
                </span>
              </div>
              <div className="flex items-center whitespace-nowrap ml-4">
                <div
                  style={(() => {
                    const pb = layoutStyle?.priceBox;
                    if (!pb || pb.enabled === false) return undefined;
                    const style: React.CSSProperties = {
                      display: 'inline-block',
                      backgroundColor: (() => {
                        const bgColor = pb.backgroundColor || '#ffffff';
                        const opacityPercent = layoutStyle?.productContainer?.backgroundImageOpacity ?? 100;
                        const alpha = Math.max(0, Math.min(1, opacityPercent / 100));
                        return alpha < 1 ? hexToRgba(bgColor, alpha) : bgColor;
                      })(),
                      borderRadius: `${pb.borderRadius ?? 8}px`,
                      paddingTop: pb.paddingTop ?? 4,
                      paddingRight: pb.paddingRight ?? 8,
                      paddingBottom: pb.paddingBottom ?? 4,
                      paddingLeft: pb.paddingLeft ?? 8,
                      marginTop: pb.marginTop ?? 0,
                      marginRight: pb.marginRight ?? 0,
                      marginBottom: pb.marginBottom ?? 0,
                      marginLeft: pb.marginLeft ?? 0,
                      overflow: 'hidden',
                    };
                    const bw = pb.borderWidth ?? 0;
                    if (bw > 0) {
                      const borderBaseColor = pb.borderColor || '#000000';
                      const opacityPercent = layoutStyle?.productContainer?.backgroundImageOpacity ?? 100;
                      const alpha = Math.max(0, Math.min(1, opacityPercent / 100));
                      const borderColor = alpha < 1 ? hexToRgba(borderBaseColor, alpha) : borderBaseColor;
                      style.border = `${bw}px solid ${borderColor}`;
                    }
                    return style;
                  })()}
                >
                  <span
                    className="text-4xl font-bold text-green-800"
                    style={getTextStyle('price')}
                  >
                    {formatBrazilianNumber(promotion.price, layoutStyle?.price?.showCurrency !== false)}
                  </span>
                </div>
              </div>
            </div>
          </div>
            );
          })()}
        </div>
      </div>
    );
  }

  // Panfleto layout preview
  if (layoutType === 'panfleto') {
    const containerStyle = layoutStyle.productContainer || {};
    const bgImage = containerStyle.backgroundImage;
    const bgVisible = containerStyle.backgroundImageVisible !== false;
    const bgImageOpacity = containerStyle.backgroundImageOpacity ?? 100;
    const shouldHideShadow = bgImage && bgVisible && bgImageOpacity === 0;
    const panfletoBg = getPanfletoBackgroundCssColor(layoutStyle.panfletoContent);
    const hasPanfletoCustomBg = panfletoHasExplicitBackgroundColor(layoutStyle.panfletoContent);
    const hasVisibleBgImage = Boolean(bgImage && bgVisible);
    /** Cor + imagem no palco: cor na base, imagem por cima (opacidade/blur). Não interfere na borda compartilhada. */
    const panfletoLayeredBg = Boolean(hasVisibleBgImage && (hasPanfletoCustomBg || bgImageOpacity < 100 || (containerStyle.backgroundImageBlur ?? 0) > 0));
    const blurPx = containerStyle.backgroundImageBlur ?? 0;
    const hasStageCustomFill = hasVisibleBgImage || hasPanfletoCustomBg;

    const baseCard = getContainerStyle() as React.CSSProperties;
    delete (baseCard as { backgroundImage?: string }).backgroundImage;
    delete (baseCard as { backgroundImageOpacity?: number }).backgroundImageOpacity;
    delete (baseCard as { backgroundImageBlur?: number }).backgroundImageBlur;
    delete (baseCard as { backgroundImageVisible?: boolean }).backgroundImageVisible;
    if (panfletoLayeredBg) {
      delete (baseCard as { backdropFilter?: string }).backdropFilter;
    }
    const cardStyle: React.CSSProperties = {
      ...baseCard,
      boxShadow: shouldHideShadow ? 'none' : undefined,
    };
    if (hasStageCustomFill) {
      cardStyle.background = 'none';
      cardStyle.backgroundImage = 'none';
      cardStyle.backgroundColor = 'transparent';
    }

    const stageStyle: React.CSSProperties = {
      width: `${PANFLETO_VIRTUAL_WIDTH}px`,
      height: `${PANFLETO_VIRTUAL_HEIGHT}px`,
      position: 'relative',
    };
    if (panfletoLayeredBg) {
      stageStyle.backgroundColor = 'transparent';
      stageStyle.backgroundImage = undefined;
    } else if (hasPanfletoCustomBg) {
      stageStyle.backgroundColor = panfletoBg;
      stageStyle.backgroundImage = undefined;
    } else {
      stageStyle.backgroundSize = 'cover';
      stageStyle.backgroundPosition = 'center';
      stageStyle.backgroundRepeat = 'no-repeat';
      stageStyle.backgroundColor = panfletoBg;
      if (hasVisibleBgImage) {
        stageStyle.backgroundImage = `url(${bgImage})`;
      }
    }
    const vignetteEnabled = containerStyle.backgroundVignetteEnabled === true;
    const vignetteIntensity = Math.max(0, Math.min(100, containerStyle.backgroundVignetteIntensity ?? 35));
    const vignetteMainAlpha = 0.22 + (vignetteIntensity / 100) * 0.78;
    const vignetteOuterAlpha = 0.06 + (vignetteIntensity / 100) * 0.34;
    const vignetteMainRadius = 120 + (vignetteIntensity / 100) * 240;
    const vignetteOuterRadius = 260 + (vignetteIntensity / 100) * 320;

    const savedPc = mergePanfletoContent(layoutStyle.panfletoContent);
    const useFreeform = panfletoHasFreeformPlacements(savedPc);
    const showPanfletoFotoLoading =
      panfletoFotoLoading || (hasVisibleBgImage && Boolean(bgImage) && !panfletoBgImageReady);

    const panfletoFotoLoadingOverlay = showPanfletoFotoLoading ? (
      <div className="absolute inset-0 z-[5] flex items-center justify-center pointer-events-none bg-black/25">
        <div className="inline-flex items-center gap-2 rounded-lg bg-white/95 px-3 py-2 text-xs font-medium text-gray-700 shadow-sm">
          <Loader className="w-4 h-4 animate-spin text-blue-500 shrink-0" />
          Carregando foto de fundo...
        </div>
      </div>
    ) : null;

    return (
      <div className="w-full border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="bg-gray-50 p-3 border-b border-gray-200">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-medium text-gray-700">Pré-visualização - Layout Panfleto</h3>
            <button
              type="button"
              onClick={() => setIsPanfletoPreviewExpanded(true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-100 text-gray-700"
              title="Abrir preview ampliado"
            >
              Preview
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <div ref={panfletoViewportRef} className="relative w-full aspect-video overflow-hidden bg-gray-100">
          {panfletoFotoLoadingOverlay}
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="relative"
              style={{
                width: `${PANFLETO_VIRTUAL_WIDTH}px`,
                height: `${PANFLETO_VIRTUAL_HEIGHT}px`,
                transform: `scale(${panfletoScale})`,
                transformOrigin: 'center center',
              }}
            >
              <div
                className={
                  hasStageCustomFill
                    ? 'p-0 relative w-full h-full'
                    : 'p-4 sm:p-8 relative w-full h-full'
                }
                style={stageStyle}
              >
                {panfletoLayeredBg ? (
                  <>
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-0 z-0"
                      style={{ backgroundColor: panfletoBg }}
                    />
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-0 z-0"
                      style={{
                        backgroundImage: `url(${String(bgImage)})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        opacity: bgImageOpacity / 100,
                        filter: blurPx > 0 ? `blur(${blurPx}px)` : undefined,
                      }}
                    />
                  </>
                ) : null}
                {vignetteEnabled ? (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 z-[1]"
                    style={{
                      boxShadow: `inset 0 0 ${vignetteMainRadius.toFixed(0)}px rgba(0,0,0,${vignetteMainAlpha.toFixed(3)}), inset 0 0 ${vignetteOuterRadius.toFixed(0)}px rgba(0,0,0,${vignetteOuterAlpha.toFixed(3)})`,
                    }}
                  />
                ) : null}
                <div
                  style={cardStyle}
                  className={`w-full h-full ${hasStageCustomFill ? 'min-h-[400px] flex flex-col' : ''} ${
                    shouldHideShadow ? '' : 'shadow-sm'
                  }`}
                >
                  <PanfletoFlyerContent
                    layoutStyle={layoutStyle}
                    promo={promotion}
                    density="display"
                    useFreeformLayout={useFreeform}
                    elementPlacements={(savedPc.elementPlacements as PanfletoElementPlacements) ?? {}}
                    getTextStyle={getTextStyle}
                    fixedFrameMode
                    panfletoHasProductsOnScreen={panfletoHasProductsOnScreen}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        {isPanfletoPreviewExpanded ? (
          <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 p-4">
            <div className="relative w-[80vw] aspect-video max-h-[80vh] rounded-xl shadow-2xl overflow-hidden">
              <button
                type="button"
                onClick={() => setIsPanfletoPreviewExpanded(false)}
                className="absolute top-0 right-0 z-[1202] inline-flex items-center justify-center h-10 w-10 rounded-bl-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
                aria-label="Fechar preview ampliado"
              >
                <X className="w-4 h-4" />
              </button>
              <div ref={panfletoOverlayViewportRef} className="absolute inset-0 overflow-hidden bg-transparent">
                {panfletoFotoLoadingOverlay}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="relative"
                    style={{
                      width: `${PANFLETO_VIRTUAL_WIDTH}px`,
                      height: `${PANFLETO_VIRTUAL_HEIGHT}px`,
                      transform: `scale(${panfletoOverlayScale})`,
                      transformOrigin: 'center center',
                    }}
                  >
                    <div
                      className={
                        hasStageCustomFill
                          ? 'p-0 relative w-full h-full'
                          : 'p-4 sm:p-8 relative w-full h-full'
                      }
                      style={stageStyle}
                    >
                      {panfletoLayeredBg ? (
                        <>
                          <div
                            aria-hidden
                            className="pointer-events-none absolute inset-0 z-0"
                            style={{ backgroundColor: panfletoBg }}
                          />
                          <div
                            aria-hidden
                            className="pointer-events-none absolute inset-0 z-0"
                            style={{
                              backgroundImage: `url(${String(bgImage)})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat',
                              opacity: bgImageOpacity / 100,
                              filter: blurPx > 0 ? `blur(${blurPx}px)` : undefined,
                            }}
                          />
                        </>
                      ) : null}
                      {vignetteEnabled ? (
                        <div
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-0 z-[1]"
                          style={{
                            boxShadow: `inset 0 0 ${vignetteMainRadius.toFixed(0)}px rgba(0,0,0,${vignetteMainAlpha.toFixed(3)}), inset 0 0 ${vignetteOuterRadius.toFixed(0)}px rgba(0,0,0,${vignetteOuterAlpha.toFixed(3)})`,
                          }}
                        />
                      ) : null}
                      <div style={cardStyle} className="w-full h-full min-h-[400px] flex flex-col">
                        <PanfletoFlyerContent
                          layoutStyle={layoutStyle}
                          promo={promotion}
                          density="display"
                          useFreeformLayout={useFreeform}
                          elementPlacements={(savedPc.elementPlacements as PanfletoElementPlacements) ?? {}}
                          getTextStyle={getTextStyle}
                          fixedFrameMode
                          panfletoHasProductsOnScreen={panfletoHasProductsOnScreen}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  // Video layout preview (simplified)
  const containerStyle = layoutStyle.productContainer || {};
  const bgImage = containerStyle.backgroundImage;
  const bgVisible = containerStyle.backgroundImageVisible !== false;
  // Quando a opacidade da imagem de fundo for 0%, remover a sombra/borda do produto
  const bgImageOpacity = containerStyle.backgroundImageOpacity ?? 100;
  const shouldHideShadow = bgImage && bgVisible && bgImageOpacity === 0;

  return (
    <div className="w-full border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      <div className="bg-gray-50 p-3 border-b border-gray-200">
        <h3 className="text-sm font-medium text-gray-700">Pré-visualização - Layout Vídeo</h3>
      </div>
      {/* Container com imagem de fundo (se houver) */}
      <div
        className="p-4 relative"
        style={{
          backgroundColor: bgImage && bgVisible ? '#000000' : '#ffffff',
          backgroundImage: bgImage && bgVisible ? `url(${bgImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          minHeight: '200px',
        }}
      >
        {/* Container do produto com opacidade/blur */}
        {(() => {
          const bgImageOpacity = containerStyle.backgroundImageOpacity ?? 100;
          const shouldHideShadow = bgImage && bgVisible && bgImageOpacity === 0;
          
          return (
            <div 
              style={{
                ...getContainerStyle(),
                // Remover boxShadow quando a opacidade for 0% para que o texto fique integrado na imagem
                boxShadow: shouldHideShadow ? 'none' : undefined,
              }} 
              className={`${shouldHideShadow ? '' : 'shadow-sm'} p-4`}
            >
              {/* Content Layer */}
              <div className="flex flex-col relative z-10">
                <h3 style={getTextStyle('productName')} className="mb-4">
                  {promotion.name} (Vídeo Promocional)
                </h3>

            <div className="bg-gray-100 rounded-lg h-32 flex items-center justify-center">
              <span className="text-gray-500">Vídeo Promocional</span>
            </div>

            {layoutStyle.category && (
              <p style={{
                ...getTextStyle('category'),
                marginTop: '8px'
              }}>
                <span className="font-medium">Categoria:</span> {promotion.category}
              </p>
            )}

            {promotion.description && layoutStyle.description && (
              <p style={{
                ...getTextStyle('description'),
                marginTop: '8px'
              }}>
                {promotion.description}
              </p>
            )}

            {layoutStyle.validUntil && (
              <p style={{
                ...getTextStyle('validUntil'),
                marginTop: '8px'
              }}>
                Válido até: {new Date(promotion.validUntil).toLocaleDateString()}
              </p>
            )}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default StylePreview; 